import {
  createSingleEventSaga,
  MyAction,
  composeSagas,
} from '@mrnkr/redux-saga-toolbox';
import { Observable, PromiseQueue } from '@mrnkr/promise-queue';
import { createActions, createReducer } from 'reduxsauce';
import { SagaIterator } from 'redux-saga';
import { call, take, select, put, race } from 'redux-saga/effects';
import Video, { Room } from 'twilio-video';
import Bugsnag from '@bugsnag/js';
import { API_URL, AUTH_API_URL } from '../config';
import { ArgsWithHeaders, LocationChangeActionPayload } from '../utils/typings';
import { Creators as LoadingActions } from './loading.module';
import { Creators as ErrorActions } from './errors.module';
import { MMDError } from '../utils/MMDError';
import { putAuthInfoInArgs } from './auth.module';
import { onRoute, extractRouteParams } from '../utils/onRoute';
import { noOpAction } from '../utils/noOpAction';
import { firestore } from '../utils/Firebase';
import { MyState } from '../store';
import { Booking, LocalMediaTracks } from '../typings';
import { doc, onSnapshot } from 'firebase/firestore';
import { selectBookingById } from './bookings.module';
import { selectProviderState } from './provider.module';

interface ActionTypes {
  REQUEST_CONNECT_VIDEO: string;
  CONNECT_VIDEO: string;
  COMMIT_LOCAL_TRACKS: string;
  COMMIT_TWILIO_TOKEN: string;
  REQUEST_DISCONNECT_VIDEO: string;
  COMMIT_IS_AUDIO_MUTED: string;
  REQUEST_MUTE_LOCAL_AUDIO_TRACK: string;
  REQUEST_UNMUTE_LOCAL_AUDIO_TRACK: string;
  CLEAR_VIDEO_STATE: string;
  VIDEO_CALL_LOG_EVENT: string;
  EXTEND_VIDEO_CALL: string;
  SUBSCRIBE_EXTENSION: string;
}
interface ActionCreators {
  requestConnectVideo: () => MyAction<object>;
  connectVideo: (payload: { room: any }) => MyAction<{ room: any }>;
  commitLocalTracks: (payload: LocalMediaTracks) => MyAction<LocalMediaTracks>;
  commitTwilioToken: (payload: {
    token: string;
    bookingId: string;
  }) => MyAction<{ token: string; bookingId: string }>;
  requestDisconnectVideo: () => MyAction<object>;
  requestMuteLocalAudioTrack: () => MyAction<void>;
  requestUnmuteLocalAudioTrack: () => MyAction<void>;
  commitIsAudioMuted: (payload: boolean) => MyAction<boolean>;
  clearVideoState: () => MyAction<object>;
  videoCallLogEvent: (payload: {
    event: string;
  }) => MyAction<{ event: string }>;
  extendVideoCall: () => MyAction<object>;
  subscribeExtension: (payload: { booking: Booking }) => MyAction<object>;
}
export type VideoCallState = {
  bookingId?: string;
  token?: string;
  room?: any;
  localTracks: LocalMediaTracks;
  isAudioMuted: boolean;
  extended?: boolean;
};
export const { Creators, Types } = createActions<ActionTypes, ActionCreators>({
  requestConnectVideo: ['payload'],
  connectVideo: ['payload'],
  commitLocalTracks: ['payload'],
  commitTwilioToken: ['payload'],
  requestDisconnectVideo: [],
  commitIsAudioMuted: ['payload'],
  requestMuteLocalAudioTrack: [],
  requestUnmuteLocalAudioTrack: [],
  clearVideoState: [],
  videoCallLogEvent: ['payload'],
  extendVideoCall: [],
  subscribeExtension: ['payload'],
});
const initialState = {
  localTracks: null,
  isAudioMuted: false,
};
function commitTwilioToken(
  state: VideoCallState,
  action: MyAction<{ token: string; bookingId: string }>,
): VideoCallState {
  return {
    ...state,
    ...action.payload,
  };
}
function connectVideo(
  state: VideoCallState,
  action: MyAction<{ room: any; bookingId: string }>,
): VideoCallState {
  return {
    ...state,
    room: action.payload.room,
  };
}
function commitLocalTracks(
  state: VideoCallState,
  action: MyAction<LocalMediaTracks>,
): VideoCallState {
  return {
    ...state,
    localTracks: action.payload,
  };
}

function extendVideoCall(state: VideoCallState): VideoCallState {
  return {
    ...state,
    extended: true,
  };
}
function clearState(): VideoCallState {
  return initialState;
}

function commitIsAudioMuted(
  state: VideoCallState,
  action: MyAction<boolean>,
): VideoCallState {
  return {
    ...state,
    isAudioMuted: action.payload,
  };
}

export const videoCallReducer = createReducer<VideoCallState>(initialState, {
  [Types.CONNECT_VIDEO]: connectVideo,
  [Types.COMMIT_LOCAL_TRACKS]: commitLocalTracks,
  [Types.COMMIT_IS_AUDIO_MUTED]: commitIsAudioMuted,
  [Types.COMMIT_TWILIO_TOKEN]: commitTwilioToken,
  [Types.EXTEND_VIDEO_CALL]: extendVideoCall,
  [Types.CLEAR_VIDEO_STATE]: clearState,
});
async function downloadTwilioToken({
  headers,
  ...payload
}: ArgsWithHeaders<{ id: string }>): Promise<
  { response: true; token: string } | { response: false }
> {
  const result = await fetch(`${AUTH_API_URL}/twilio/token/${payload.id}`, {
    headers,
    method: 'GET',
  });
  if (!result.ok) {
    throw new MMDError(
      'Something went wrong connecting you to your conference',
    );
  }
  return result.json();
}
const requestVideoCallTokenWatcher = createSingleEventSaga<
  LocationChangeActionPayload,
  { token: string; bookingId: string },
  MyAction<LocationChangeActionPayload>
>({
  takeEvery: onRoute('/video-call/:id'),
  loadingAction: LoadingActions.setLoading,
  commitAction: Creators.commitTwilioToken,
  successAction: noOpAction,
  errorAction: ErrorActions.setError,
  action: downloadTwilioToken,
  beforeAction: composeSagas<
    LocationChangeActionPayload,
    { id: string },
    ArgsWithHeaders<{ id: string }>
  >(
    // @ts-ignore
    extractRouteParams('/video-call/:id'),
    putAuthInfoInArgs,
  ),
  // @ts-ignore
  *afterAction(
    res: { response: true; token: string } | { response: false },
    payload: LocationChangeActionPayload,
  ): SagaIterator {
    if (res.response) {
      const params = yield call(extractRouteParams('/video-call/:id'), payload);
      return { token: res.token, bookingId: params.id };
    }
  },
});
function* connect({ bookingId, token }): SagaIterator {
  try {
    const room = yield call([Video, 'connect'], token, { name: bookingId });

    return {
      room,
      bookingId,
    };
  } catch (err) {
    const { email } = yield select(selectProviderState);
    Bugsnag.notify(err, (event) => {
      event.context = `video call (common) failed to connect ( bookingId: ${bookingId} )`;
      event.setUser(email, email);
      event.addMetadata('video call', { bookingId, flow: 'common' });
    });
    yield put(Creators.videoCallLogEvent({ event: 'onRoomDidFailToConnect' }));
    throw new MMDError(
      'We were unable to connect you to your meeting for we do not have permission to use your camera and/or microphone. ' +
        "Please, grant us permission to use them or else we won't be able to connect.",
    );
  }
}

function* createLocalTracks({
  headers,
  booking,
}: ArgsWithHeaders<{ booking: Booking }>) {
  const room: Room = yield select((state: MyState) => state.videoCall.room);

  const localVideoTracks = Array.from(
    room.localParticipant.videoTracks.values(),
  ).map((track) => track.track);

  const localAudioTrack = Array.from(
    room.localParticipant.audioTracks.values(),
  ).map((track) => track.track);

  if (booking && !booking.doctorAttended) {
    yield call(fetch, `${API_URL}/bookings/${booking.eventId}`, {
      headers,
      method: 'PUT',
      body: JSON.stringify({ doctorAttended: true }),
    });
  }

  return {
    video: localVideoTracks[0],
    audio: localAudioTrack[0],
  };
}

const requestConnectVideoWatcher = createSingleEventSaga<
  object,
  any,
  MyAction<object>
>({
  takeEvery: Types.COMMIT_TWILIO_TOKEN,
  loadingAction: LoadingActions.setLoading,
  commitAction: Creators.connectVideo,
  successAction: noOpAction,
  errorAction: ErrorActions.setError,
  action: connect,
  // @ts-ignore
  *beforeAction(payload: { bookingId: string }): SagaIterator {
    const token = yield select((state: MyState) => state.videoCall.token);
    return { ...payload, token };
  },
});

const requestLocalTracksWatcher = createSingleEventSaga<
  object,
  LocalMediaTracks,
  MyAction<object>
>({
  takeEvery: Types.CONNECT_VIDEO,
  loadingAction: LoadingActions.setLoading,
  commitAction: Creators.commitLocalTracks,
  successAction: noOpAction,
  errorAction: ErrorActions.setError,
  action: createLocalTracks,
  // @ts-ignore
  *beforeAction({ bookingId }): SagaIterator {
    const { headers } = yield call(putAuthInfoInArgs, {});

    const booking = yield select((state: MyState) =>
      selectBookingById(state, bookingId),
    );

    return {
      booking,
      headers,
    };
  },
});
const requestDisconnectWatcher = createSingleEventSaga<
  object,
  void,
  MyAction<object>
>({
  takeEvery: Types.REQUEST_DISCONNECT_VIDEO,
  loadingAction: LoadingActions.setLoading,
  commitAction: Creators.clearVideoState,
  successAction: noOpAction,
  errorAction: ErrorActions.clearError,
  *action(): SagaIterator {
    const { room, localTracks, bookingId } = yield select(
      (state: MyState) => state.videoCall,
    );
    const { email } = yield select(selectProviderState);
    Bugsnag.leaveBreadcrumb('video call (common): disconnecting room', {
      bookingId,
      email,
    });
    room.disconnect();
    localTracks.audio.stop();
    localTracks.video.stop();
    localTracks.video.detach().forEach((detachedElement) => {
      detachedElement.remove();
    });
  },
});

const requestMuteLocalAudioTrackWatcher = createSingleEventSaga<
  boolean,
  void,
  MyAction<boolean>
>({
  takeEvery: Types.REQUEST_MUTE_LOCAL_AUDIO_TRACK,
  loadingAction: noOpAction,
  commitAction: Creators.commitIsAudioMuted,
  successAction: noOpAction,
  errorAction: ErrorActions.clearError,
  *action(): SagaIterator {
    const { audio }: LocalMediaTracks = yield select(
      (state: MyState) => state.videoCall.localTracks,
    );

    audio.disable();

    return true;
  },
});

const requestUnmuteLocalAudioTrackWatcher = createSingleEventSaga<
  boolean,
  void,
  MyAction<boolean>
>({
  takeEvery: Types.REQUEST_UNMUTE_LOCAL_AUDIO_TRACK,
  loadingAction: noOpAction,
  commitAction: Creators.commitIsAudioMuted,
  successAction: noOpAction,
  errorAction: ErrorActions.clearError,
  *action(): SagaIterator {
    const { audio }: LocalMediaTracks = yield select(
      (state: MyState) => state.videoCall.localTracks,
    );

    audio.enable();

    return false;
  },
});

export const getParticipantType = (id: string, booking: Booking) =>
  new Map([
    [true, 'Student'],
    [id === booking.doctorId, 'Provider'],
    [id === booking.patientId, 'Patient'],
  ]).get(true);

function* logEvent(): SagaIterator {
  while (true) {
    try {
      const {
        payload: { event: action },
      } = yield take(Types.VIDEO_CALL_LOG_EVENT);
      const { bookingId } = yield select((state: MyState) => state.videoCall);

      const booking = yield select((state: MyState) =>
        selectBookingById(state, bookingId),
      );

      const { id } = yield select(selectProviderState);

      const actor = yield call(getParticipantType, id, booking);

      const { headers } = yield call(putAuthInfoInArgs, {});
      const result = yield call(
        fetch,
        `${AUTH_API_URL}/bookings/${bookingId}/timeTracking`,
        {
          headers,
          method: 'POST',
          body: JSON.stringify({ action, actor }),
        },
      );
      if (!result.ok) {
        throw new MMDError('An error has occurred');
      }
    } catch (err) {
      yield put(ErrorActions.setError(err));
    }
  }
}

function extensionStatus(args: {
  bookingId: number;
  doctorId: string;
  patientId: string;
}): Observable<boolean> {
  return {
    subscribe: (next, error) => {
      const bookingExtensionsRef = doc(
        firestore,
        'bookingExtensions',
        `${args.bookingId}`,
        'doctors',
        args.doctorId,
        'patients',
        args.patientId,
      );

      const unsubscribe = onSnapshot(
        bookingExtensionsRef,
        (snap) => {
          if (snap.exists()) {
            next(snap.exists());
          }
        },
        error,
      );
      return { unsubscribe };
    },
  };
}

function* extensionStatusWatcher(): SagaIterator {
  while (true) {
    const action = yield take(Types.SUBSCRIBE_EXTENSION);
    const booking: Booking = action.payload.booking;
    const { id: bookingId, doctorId, patientId } = booking;
    try {
      // Will emit only once - REMEMBER
      const promiseQueue = new PromiseQueue<boolean>(
        extensionStatus({
          bookingId: parseInt(bookingId, 10),
          doctorId,
          patientId,
        }),
      );
      const { leave, extend } = yield race({
        extend: call(promiseQueue.next),
        leave: take(
          (action: MyAction<{ event: string }>) =>
            action.type === Types.CLEAR_VIDEO_STATE,
        ),
      });
      if (leave) {
        promiseQueue.cancel(new Error());
        continue;
      }
      if (extend && extend.value) {
        yield put(Creators.extendVideoCall());
        promiseQueue.cancel(new Error());
      }
    } catch (err) {
      const { email } = yield select(selectProviderState);
      Bugsnag.notify(err, (event) => {
        event.context = `video call (common) extension status subscription error ( bookingId: ${bookingId} )`;
        event.setUser(email, email);
        event.addMetadata('video call', { bookingId, flow: 'common' });
      });
    }
  }
}

export const videoCallSagas = [
  requestVideoCallTokenWatcher,
  requestConnectVideoWatcher,
  requestLocalTracksWatcher,
  requestDisconnectWatcher,
  extensionStatusWatcher,
  requestMuteLocalAudioTrackWatcher,
  requestUnmuteLocalAudioTrackWatcher,
  logEvent,
];
